/**
 * How brick layers look, per theme brick style (docs/THEMES.md).
 *
 * A layer is a full-width row of chunky, rounded bricks drawn as elements
 * (so each brick can have its own tone and texture offset) with the theme's
 * mortar colour showing in the gaps. Everything varies deterministically with
 * the layer index — the same stack always looks the same, and a new layer
 * never makes the ones below it change.
 *
 * Colours come from CSS (--ds-brick-a / -b / -mortar); this module only picks
 * shapes: how many bricks, how the joints line up, a tone mix per brick, and
 * which particle shape a breaking layer bursts into.
 */
import type { BrickStyle } from '@dropstack/core';

export const BRICK_STYLES: readonly BrickStyle[] = [
  'stone',
  'wood',
  'coral',
  'ice',
  'candy',
  'sandstone',
  'bamboo',
  'crystal',
  'clay',
  'mossy',
  'shell',
  'marble',
];

/** Shape of the bits a breaking layer bursts into. */
export type ParticleShape = 'chunk' | 'splinter' | 'bubble' | 'shard' | 'diamond';

export interface BrickSpec {
  /** Bricks across one full layer (before the bond's half bricks). */
  perRow: number;
  /**
   * How joints line up between layers: `running` = the classic half-brick
   * offset every other layer; `jitter` = uneven lengths (planks, coral,
   * crystals); `stack` = joints straight above each other (bamboo poles).
   */
  bond: 'running' | 'jitter' | 'stack';
  particle: ParticleShape;
}

export const BRICK_SPECS: Readonly<Record<BrickStyle, BrickSpec>> = {
  stone: { perRow: 4, bond: 'running', particle: 'chunk' },
  wood: { perRow: 2, bond: 'jitter', particle: 'splinter' },
  coral: { perRow: 5, bond: 'jitter', particle: 'bubble' },
  ice: { perRow: 3, bond: 'running', particle: 'shard' },
  candy: { perRow: 4, bond: 'running', particle: 'bubble' },
  sandstone: { perRow: 3, bond: 'running', particle: 'chunk' },
  bamboo: { perRow: 4, bond: 'stack', particle: 'splinter' },
  crystal: { perRow: 4, bond: 'jitter', particle: 'diamond' },
  clay: { perRow: 5, bond: 'running', particle: 'chunk' },
  mossy: { perRow: 4, bond: 'running', particle: 'chunk' },
  shell: { perRow: 5, bond: 'running', particle: 'bubble' },
  marble: { perRow: 3, bond: 'running', particle: 'shard' },
};

export interface BrickLook {
  /** Relative width (flex-grow) within the layer. */
  grow: number;
  /** Mix of --ds-brick-b into --ds-brick-a, as a CSS percentage ("0%"–"100%"). */
  tone: string;
  /** Texture offset for this brick, as a CSS percentage, so neighbours never look copy-pasted. */
  grain: string;
}

export interface BrickLayerLook {
  className: string;
  bricks: BrickLook[];
}

/** Small deterministic integer hash → [0, 1). */
export function hash01(...parts: number[]): number {
  let h = 0x9e3779b9;
  for (const p of parts) {
    h = Math.imul(h ^ Math.trunc(p), 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
  }
  return (h >>> 0) / 4294967296;
}

const STYLE_SALT: Readonly<Record<BrickStyle, number>> = Object.fromEntries(BRICK_STYLES.map((s, i) => [s, 101 + i * 37])) as Record<
  BrickStyle,
  number
>;

const pct = (x: number) => `${Math.round(x * 100)}%`;

/** Relative brick widths for one layer. */
function brickWidths(spec: BrickSpec, index: number, salt: number): number[] {
  const n = spec.perRow;
  switch (spec.bond) {
    case 'stack':
      return Array.from({ length: n }, () => 1);
    case 'running':
      return index % 2 === 0 ? Array.from({ length: n }, () => 1) : [0.5, ...Array.from({ length: n - 1 }, () => 1), 0.5];
    case 'jitter':
      // Lengths between 0.6 and 1.4 of an average brick.
      return Array.from({ length: n }, (_, i) => 0.6 + 0.8 * hash01(salt, index, i, 7));
  }
}

/** The look of layer `index` (0 = bottom) in `style`. Deterministic. */
export function brickLayerLook(style: BrickStyle, index: number): BrickLayerLook {
  const spec = BRICK_SPECS[style];
  const salt = STYLE_SALT[style];
  const bricks = brickWidths(spec, index, salt).map((grow, i) => {
    // Alternate the two brick colours, softened by a random mix so no two neighbours are identical.
    const base = (index + i) % 2 === 0 ? 0.15 : 0.7;
    const tone = Math.min(1, Math.max(0, base + (hash01(salt, index, i, 1) - 0.5) * 0.3));
    return { grow: Math.round(grow * 1000) / 1000, tone: pct(tone), grain: pct(hash01(salt, index, i, 2)) };
  });
  return { className: `brick-layer brick-layer--${style}`, bricks };
}

// ───────────── Burst particles ─────────────

export interface BurstParticle {
  /** Horizontal travel, −1 … 1 (× the burst's spread). */
  dx: number;
  /** Vertical travel, −1 (up) … 0.35 (a little down). */
  dy: number;
  /** Spin (degrees). */
  rotate: number;
  /** Size multiplier, 0.6 … 1.3. */
  scale: number;
  /** Start delay (ms). */
  delay: number;
  /** Which theme colour: 0 = brick A, 1 = brick B, 2 = accent. */
  color: 0 | 1 | 2;
  /** Where along the layer it starts, 0 … 1. */
  x: number;
}

/** A deterministic burst of `count` particles (seeded by the problem, so a replay looks identical). */
export function burstParticles(seed: number, count: number): BurstParticle[] {
  return Array.from({ length: count }, (_, i) => {
    const r = (k: number) => hash01(seed, i, k);
    const x = (i + 0.5) / count;
    return {
      x,
      dx: Math.max(-1, Math.min(1, (x - 0.5) * 1.2 + (r(1) - 0.5) * 0.8)),
      dy: -0.35 - 0.65 * r(2) + (r(3) < 0.2 ? 0.7 : 0),
      rotate: Math.round((r(4) - 0.5) * 360),
      scale: 0.6 + 0.7 * r(5),
      delay: Math.round(r(6) * 60),
      color: (i % 3) as 0 | 1 | 2,
    };
  });
}
